import React, { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";
import { useActivePageDomNodeRefMapRegistration } from "../../../../hooks/activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { isWebsiteBuilderContainer, isWebsiteBuilderContainerWithBuilder, isWebsiteBuilderStretchContainerData, TWebsiteBuilderContainer, WEBSITE_BUILDER_CONTAINER_CLASS_NAME } from "../../../../nodes/container/websiteBuilderContainer";
import { TWebsiteBuilderSection, websiteBuilderGetSectionScreenPosition } from "../../../../nodes/section/websiteBuilderSection";
import { useWebsiteBuilderEditorContext } from "../../../../websiteBuilderEditorContext";
import { isWebsiteBuilderFlex } from "../../../../nodes/container/websiteBuilderFlex";
import { isWebsiteBuilderGrid } from "../../../../nodes/container/websiteBuilderGrid";
import { isWebsiteBuilderForm } from "../../../../nodes/container/websiteBuilderForm";
import { isWebsiteBuilderTable } from "../../../../nodes/container/websiteBuilderTable";
import { isWebsiteBuilderSlideshow } from "../../../../nodes/container/websiteBuilderSlideshow";
import { WebsiteBuilderFlexView } from "./flex/WebsiteBuilderFlexView";
import { WebsiteBuilderGridView } from "./grid/WebsiteBuilderGridView";
import { WebsiteBuilderFormView } from "./form/WebsiteBuilderFormView";
import { WebsiteBuilderTableView } from "./table/WebsiteBuilderTableView";
import { WebsiteBuilderSlideshowView } from "./slideshow/WebsiteBuilderSlideshowView";
import { isWebsiteBuilderElementContainerParentNode, TWebsiteBuilderDraggableNode, TWebsiteBuilderDraggableNodeDropItemType } from "../../../../nodes/websiteBuilderNode";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import classNames from "classnames";
import { useWebsiteBuilderContainerMetadata } from "../../../../hooks/containerMetadata/useWebsiteBuilderContainerMetadata";
import { websiteBuilderSelectNode, websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { IUseContainerViewContextMenuContentArgs, useContainerViewContextMenuContent } from "../../hooks/container/useContainerViewContextMenuContent/useContainerViewContextMenuContent";
import { useDrop } from "react-dnd";
import { IUseSectionEntityViewSettingsArgs, useSectionEntityViewSettings } from "../../hooks/sectionEntity/useSectionEntityViewSettings/useSectionEntityViewSettings";
import { IUseSectionEntityViewStretchSettingsArgs, useSectionEntityViewStretchSettings } from "../../hooks/sectionEntity/useSectionEntityViewStretchSettings/useSectionEntityViewStretchSettings";
import { IUseSectionEntityViewContextMenuArgs, useSectionEntityViewContextMenu } from "../../hooks/sectionEntity/useSectionEntityViewContextMenu/useSectionEntityViewContextMenu";
import { IWebsiteBuilderSectionEntityViewBoundingBox } from "../../hooks/sectionEntity/helpers/sectionEntityViewBoundingBox";
import { websiteBuilderInsertElementIntoFlexContainer } from "../../../../hooks/nodeOperations/helpers/sectionEntityOperations";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../../../utility/websiteBuilderZLayers";
import { TUseSectionEntityViewResizeArgs, useSectionEntityViewResize } from "../../hooks/sectionEntity/useSectionEntityViewResize/useSectionEntityViewResize";
import { IWebsiteBuilderSectionEntityLayoutData, IWebsiteBuilderSectionEntityStretch, websiteBuilderGetSectionEntityActiveLayoutData } from "../../../../nodes/websiteBuilderSectionEntity";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "../../../../nodes/websiteBuilderNodeGeometry";
import { IUseSectionEntityViewMoveArgs, useSectionEntityViewMove } from "../../hooks/sectionEntity/useSectionEntityViewMove/useSectionEntityViewMove";
import { TWebsiteBuilderContainerViewContext } from "../../hooks/sectionEntity/helpers/sectionEntityViewContext";
import { SectionEntityFrame, TSectionEntityFrameHighlight } from "../sectionEntity/SectionEntityFrame";
import { isWebsiteBuilderElement, isWebsiteBuilderStretchElementData } from "../../../../nodes/element/websiteBuilderElement";
import { useWebsiteBuilderDndManager } from "../../../../hooks/dndManager/useWebsiteBuilderDndManager";
import { IWebsiteBuilderRoot } from "../../../../nodes/root/websiteBuilderRoot";
import { isWebsiteBuilderBox } from "../../../../nodes/container/websiteBuilderBox";
import { WebsiteBuilderBoxView } from "./box/WebsiteBuilderBoxView";
import { IWebsiteBuilderDropSectionEntityInFreeformDomainRect, IWebsiteBuilderFreeformParentContext, websiteBuilderDropSectionEntityInFreeform } from "../../../../nodes/websiteBuilderFreeformParentNode";
import { SectionEntityPreviewProvider } from "../sectionEntity/SectionEntityPreviewContext";
import { isWebsiteBuilderTabset } from "../../../../nodes/container/websiteBuilderTabset";
import { WebsiteBuilderTabsetView } from "./tabset/WebsiteBuilderTabsetView";
import { WebsiteBuilderTabsetTabSelector } from "./tabset/WebsiteBuilderTabsetTabSelector";
import { isWebsiteBuilderMarquee } from "../../../../nodes/container/websiteBuilderMarquee";
import { WebsiteBuilderMarqueeView } from "./marquee/WebsiteBuilderMarqueeView";

interface IWebsiteBuilderContainerViewProps {
  containerContext: TWebsiteBuilderContainerViewContext;
  children: ReactNode;
}

interface IWebsiteBuilderContainerViewDropState {
  containerIsDropTarget: boolean;
}

const ACCEPTED_CONTAINER_DROP_ITEMS: TWebsiteBuilderDraggableNodeDropItemType[] = ["website-builder-element"];

const CONTAINER_VIEW_MOTION_OPACITY = 0.7;

function WebsiteBuilderContainerView(props: IWebsiteBuilderContainerViewProps) {
  const { containerContext, children } = props;

  const dispatch = useAppDispatch();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);
  
  const { activePageDomNodeRefMap, overlayLinesRef } = useWebsiteBuilderEditorContext();

  const activePageDomNodeRefMapRegistration = useActivePageDomNodeRefMapRegistration(containerContext.node.id, activePageDomNodeRefMap);
  
  const dndManager = useWebsiteBuilderDndManager();

  const containerType = containerContext.node.container.type;

  const containerMetadata = useWebsiteBuilderContainerMetadata();
  const containerTitle = containerMetadata[containerType].title;
  
  const selectedNodeContainerId = selectedNode?.type === "element" ? selectedNode.containerId : null;

  const containerBuilderUIPortalId = `WEBSITE_BUILDER_CONTAINER_BUILDER_UI_PORTAL_${containerContext.node.id}`;

  const [{ containerIsDropTarget }, drop] = useDrop<TWebsiteBuilderDraggableNode, unknown, IWebsiteBuilderContainerViewDropState>(
    () => ({
      accept: ACCEPTED_CONTAINER_DROP_ITEMS,
      drop: (item, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        if (item.type === "element") {
          if (isWebsiteBuilderFlex(containerContext.node)) {
            websiteBuilderInsertElementIntoFlexContainer(item, containerContext.node, containerContext.parentNode.id, activePageId, deviceView, dispatch);
          } else if (isWebsiteBuilderBox(containerContext.node)) {
            const targetElement = activePageDomNodeRefMap.current.get(containerContext.node.id);
            const clientOffset = monitor.getClientOffset();

            const targetBoundingRect = targetElement?.getBoundingClientRect();

            if (targetBoundingRect == null || clientOffset == null) {
              return;
            }

            const domainRect: IWebsiteBuilderDropSectionEntityInFreeformDomainRect = {
              left: targetBoundingRect.left,
              top: targetBoundingRect.top,
              width: targetBoundingRect.width,
              height: targetBoundingRect.height,
            };

            const parentContext: IWebsiteBuilderFreeformParentContext = {
              node: containerContext.node,
              bounds: {
                desktop: containerContext.node.container.layout.desktop.dimensions,
                tablet: containerContext.node.container.layout.tablet.dimensions,
                mobile: containerContext.node.container.layout.mobile.dimensions,
              },
              ancestry: {
                sectionId: containerContext.parentNode.id,
                containerId: containerContext.node.id,
                gridCellId: null,
                tabId: null,
              },
            };

            websiteBuilderDropSectionEntityInFreeform(
              item,
              parentContext,
              domainRect,
              clientOffset,
              activePageId,
              deviceView,
              overlayLinesRef,
              dispatch,
            );
          }
        }
      },
      canDrop: item => {
        return (
          isWebsiteBuilderElementContainerParentNode(containerContext.node) &&
          item.type === "element" && (
            !isWebsiteBuilderStretchElementData(item.element) ||
            !item.element.stretch.enabled
          )
        );
      },
      collect: (monitor): IWebsiteBuilderContainerViewDropState => {
        return {
          containerIsDropTarget: monitor.canDrop() && monitor.isOver(),
        };
      },
    }),
    [
      containerContext.node,
      containerContext.parentNode.id,
      activePageId,
      deviceView,
    ],
  );

  const containerIsMoveTarget =
    isWebsiteBuilderElementContainerParentNode(containerContext.node) &&
    selectedNode?.editingMode?.type === "element-move" &&
    selectedNode.editingMode.containerMoveTargetId === containerContext.node.id;
  
  const {
    containerSelected,
    containerMoving,
    containerResizing,
    containerSettingsActive,
    containerStretchSettingsActive,
    containerBuilderActive,
    containerContextMenuActive,
    elementChildSelected,
    elementChildMoving,
  } = useMemo(() => {
    let containerSelected = false;
    let containerMoving = false;
    let containerResizing = false;
    let containerSettingsActive = false;
    let containerStretchSettingsActive = false;
    let containerBuilderActive = false;
    let containerContextMenuActive = false;

    let elementChildSelected = false;
    let elementChildMoving = false;
    
    if (selectedNode?.type === "container") {
      containerSelected = containerContext.node.id === selectedNode.id;

      if (containerSelected) {
        const editingModeType = selectedNode.editingMode.type;

        containerMoving = editingModeType === "container-move";
        containerResizing = editingModeType === "container-resize";
        containerSettingsActive = editingModeType === "container-settings";
        containerStretchSettingsActive = editingModeType === "container-stretch-settings";
        containerBuilderActive = editingModeType === "container-builder";
        containerContextMenuActive = editingModeType === "container-context-menu";
      }
    }

    if (selectedNode?.type === "element") {
      elementChildSelected = containerContext.node.id === selectedNodeContainerId;
      
      if (elementChildSelected) {
        const editingModeType = selectedNode.editingMode.type;

        elementChildMoving = editingModeType === "element-move";
      }
    }

    return {
      containerSelected,
      containerMoving,
      containerResizing,
      containerSettingsActive,
      containerStretchSettingsActive,
      containerBuilderActive,
      containerContextMenuActive,
      elementChildSelected,
      elementChildMoving,
    };
  }, [
    selectedNode?.type,
    selectedNode?.id,
    selectedNode?.editingMode?.type,
    selectedNodeContainerId,
    containerContext.node.id,
  ]);

  const [containerHovered, setContainerHovered] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      if (elementChildMoving) {
        setContainerHovered(false);
      }
    };
  }, [elementChildMoving]);

  function getContainerHighlight(): TSectionEntityFrameHighlight {
    const draggingElement = dndManager.isDragging && isWebsiteBuilderElement(dndManager.item);
    const draggingContainer = dndManager.isDragging && isWebsiteBuilderContainer(dndManager.item);

    const strongHighlight = !containerSelected && (containerIsDropTarget || containerIsMoveTarget);

    const weakHighlight = !containerSelected && (
      elementChildSelected ||
      containerHovered ||
      draggingElement ||
      draggingContainer ||
      selectedNode?.editingMode?.type === "element-move" ||
      selectedNode?.editingMode?.type === "element-resize" ||
      selectedNode?.editingMode?.type === "container-move" ||
      selectedNode?.editingMode?.type === "container-resize" ||
      selectedNode?.editingMode?.type === "section-resize"
    );

    if (strongHighlight) {
      return "strong";
    } else if (weakHighlight) {
      return "weak";
    } else {
      return "none";
    }
  }

  const containerHighlight = getContainerHighlight();

  const viewResizeArgs: TUseSectionEntityViewResizeArgs = useMemo(
    () => ({
      context: containerContext,
      selected: containerSelected,
      resizing: containerResizing,
      filled: false,
    }),
    [
      containerContext,
      containerSelected,
      containerResizing,
    ],
  );

  const { viewResizePosition, viewResizeDimensions, viewResizeHandles, viewResizeIndicator } =
    useSectionEntityViewResize(viewResizeArgs);

  const viewMoveArgs: IUseSectionEntityViewMoveArgs = useMemo(
    () => ({
      context: containerContext,
      moving: containerMoving,
      filled: false,
    }),
    [
      containerContext,
      containerMoving,
    ],
  );

  const { viewMovePosition, viewMoveDimensions, viewMoveIndicator, handleViewMovePointerDown } = useSectionEntityViewMove(viewMoveArgs);

  const viewSettingsArgs: IUseSectionEntityViewSettingsArgs = useMemo(
    () => ({
      node: containerContext.node,
      selected: containerSelected,
      settingsActive: containerSettingsActive,
    }),
    [containerContext.node, containerSelected, containerSettingsActive],
  );

  const { viewSettings, handleViewSettingsDoubleClick } = useSectionEntityViewSettings(viewSettingsArgs);

  const viewStretchSettingsArgs: IUseSectionEntityViewStretchSettingsArgs = useMemo(
    () => ({
      node: containerContext.node,
      selected: containerSelected,
      settingsActive: containerStretchSettingsActive,
      parentIsSection: containerContext.parentNode.type === "section",
    }),
    [containerContext.node, containerSelected, containerStretchSettingsActive, containerContext.parentNode.type],
  );

  const { viewStretchSettings, handleViewStretchSettingsClick } = useSectionEntityViewStretchSettings(viewStretchSettingsArgs);

  const viewContextMenuContentArgs: IUseContainerViewContextMenuContentArgs = useMemo(() => {
    return {
      node: containerContext.node,
      parentNode: containerContext.parentNode,
      menuActive: containerContextMenuActive,
      openStretchSettings: handleViewStretchSettingsClick,
    };
  }, [containerContext.node, containerContext.parentNode, containerContextMenuActive, handleViewStretchSettingsClick]);

  const { sections: containerContextMenuSections } = useContainerViewContextMenuContent(viewContextMenuContentArgs);

  const viewContextMenuArgs: IUseSectionEntityViewContextMenuArgs = useMemo(() => {
    return {
      node: containerContext.node,
      selected: containerSelected,
      menuActive: containerContextMenuActive,
      sections: containerContextMenuSections,
    };
  }, [containerContext.node, containerSelected, containerContextMenuActive, containerContextMenuSections]);

  const { viewContextMenu, handleViewOpenContextMenu } = useSectionEntityViewContextMenu(viewContextMenuArgs);

  function getContainerBoundingBox(): IWebsiteBuilderSectionEntityViewBoundingBox {
    let position: IWebsiteBuilderNodePosition = containerContext.activeScreenLayoutData.position;
    let dimensions: IWebsiteBuilderNodeDimensions = containerContext.activeScreenLayoutData.dimensions;
  
    if (containerResizing) {
      position = viewResizePosition;
      dimensions = viewResizeDimensions;
    } else if (containerMoving) {
      position = viewMovePosition;
      dimensions = viewMoveDimensions;
    }

    return {
      x: position.x,
      y: position.y,
      width: dimensions.width,
      height: dimensions.height,
    };
  }

  const containerBoundingBox = getContainerBoundingBox();

  function getContainerViewPrecedence(): CSSProperties {
    let opacity: number = 1;
    let zIndex: number = containerContext.zIndex;

    if (containerMoving || containerResizing) {
      opacity = CONTAINER_VIEW_MOTION_OPACITY;
    }

    if (containerMoving || containerResizing || containerSettingsActive || containerStretchSettingsActive || containerBuilderActive || containerContextMenuActive || elementChildMoving) {
      zIndex = WEBSITE_BUILDER_Z_LAYERS.designSpace.sectionEntityOverride;
    }

    return {
      opacity,
      zIndex,
    };
  }
  
  function renderChild(): JSX.Element {
    if (isWebsiteBuilderBox(containerContext.node)) {
      return (
        <WebsiteBuilderBoxView node={containerContext.node} elementChildMoving={elementChildMoving}>
          {children}
        </WebsiteBuilderBoxView>
      );
    } else if (isWebsiteBuilderFlex(containerContext.node)) {
      return (
        <WebsiteBuilderFlexView node={containerContext.node}>
          {children}
        </WebsiteBuilderFlexView>
      );
    } else if (isWebsiteBuilderGrid(containerContext.node)) {
      return (
        <WebsiteBuilderGridView node={containerContext.node}>
          {children}
        </WebsiteBuilderGridView>
      );
    } else if (isWebsiteBuilderMarquee(containerContext.node)) {
      return (
        <WebsiteBuilderMarqueeView node={containerContext.node} />
      );
    } else if (isWebsiteBuilderSlideshow(containerContext.node)) {
      return (
        <WebsiteBuilderSlideshowView node={containerContext.node} />
      );
    } else if (isWebsiteBuilderForm(containerContext.node)) {
      return (
        <WebsiteBuilderFormView node={containerContext.node} />
      );
    } else if (isWebsiteBuilderTable(containerContext.node)) {
      return (
        <WebsiteBuilderTableView
          node={containerContext.node}
          containerBoundingBox={containerBoundingBox}
          containerBuilderUIPortalId={containerBuilderUIPortalId}
        />
      );
    } else if (isWebsiteBuilderTabset(containerContext.node)) {
      return (
        <WebsiteBuilderTabsetView node={containerContext.node} elementChildMoving={elementChildMoving}>
          {children}
        </WebsiteBuilderTabsetView>
      );
    } else {
      return null;
    }
  }

  return (
    <>
      <div
        ref={el => {
          drop(el);
          activePageDomNodeRefMapRegistration(el);
        }}
        className={classNames(WEBSITE_BUILDER_CONTAINER_CLASS_NAME, "editor-container", {
          "container-selected": containerSelected,
          "container-builder": containerBuilderActive,
        })}
        style={{
          left: containerBoundingBox.x,
          top: containerBoundingBox.y,
          width: containerBoundingBox.width,
          height: containerBoundingBox.height,
          ...getContainerViewPrecedence(),
        }}
        onContextMenu={e => {
          e.stopPropagation();
          e.preventDefault();

          if (!containerBuilderActive) {
            handleViewOpenContextMenu({ x: e.clientX, y: e.clientY });
          }
        }}
        onMouseOver={() => setContainerHovered(true)}
        onMouseOut={() => setContainerHovered(false)}
        onDoubleClick={e => {
          e.stopPropagation();

          if (isWebsiteBuilderContainerWithBuilder(containerContext.node)) {
            if (!containerBuilderActive) {
              dispatch(
                websiteBuilderUpdateSelectedNode({
                  selectedNodeUpdates: {
                    type: "container",
                    editingMode: {
                      type: "container-builder",
                    },
                  },
                }),
              );
            }
          } else {
            handleViewSettingsDoubleClick();
          }
        }}
        onPointerDown={e => {
          e.stopPropagation();

          if (containerBuilderActive) {
            return;
          }

          if (!containerSelected) {
            dispatch(
              websiteBuilderSelectNode({
                selectedNode: {
                  type: "container",
                  sectionId: containerContext.parentNode.id,
                  id: containerContext.node.id,
                  editingMode: {
                    type: "none",
                  },
                },
              }),
            );
          }

          if (e.button === 0) {
            if (containerContextMenuActive) {
              dispatch(
                websiteBuilderUpdateSelectedNode({
                  selectedNodeUpdates: {
                    type: "container",
                    editingMode: {
                      type: "none",
                    },
                  },
                }),
              );
            }

            handleViewMovePointerDown(e);
          }
        }}
      >
        {renderChild()}
        {!containerBuilderActive && (
          <>
            {viewMoveIndicator}
            {viewResizeIndicator}
            {viewSettings}
            {viewStretchSettings}
            {viewContextMenu}
          </>
        )}
      </div>

      <SectionEntityFrame
        context={containerContext}
        title={containerTitle}
        highlight={containerHighlight}
        boundingBox={containerBoundingBox}
        selected={containerSelected}
        moving={containerMoving}
        viewResizeHandles={viewResizeHandles}
      />

      {isWebsiteBuilderTabset(containerContext.node) && (
        <WebsiteBuilderTabsetTabSelector
          node={containerContext.node}
          boundingBox={containerBoundingBox}
        />
      )}

      <div
        id={containerBuilderUIPortalId}
        style={{
          position: "absolute",
          left: containerBoundingBox.x,
          top: containerBoundingBox.y,
        }}
      />
    </>
  );
}

interface IWebsiteBuilderContainerViewContextProviderProps {
  node: TWebsiteBuilderContainer;
  parentNode: TWebsiteBuilderSection;
  grandParentNode: IWebsiteBuilderRoot,
  children: ReactNode;
}

export function WebsiteBuilderContainerViewContextProvider(props: IWebsiteBuilderContainerViewContextProviderProps) {
  const { node, parentNode, grandParentNode, children } = props;

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const { designSpaceBoundaries } = useWebsiteBuilderEditorContext();

  const containerStretch: IWebsiteBuilderSectionEntityStretch = isWebsiteBuilderStretchContainerData(node.container)
    ? node.container.stretch
    : null;

  const containerZIndex = node.container.zIndex;
  
  const containerContext: TWebsiteBuilderContainerViewContext = useMemo(() => {
    const layout = node.container.layout;
    const activeLayoutData = websiteBuilderGetSectionEntityActiveLayoutData(node, deviceView);

    const activeScreenLayoutData: IWebsiteBuilderSectionEntityLayoutData = {
      position: websiteBuilderGetSectionScreenPosition(designSpaceBoundaries.leftX, activeLayoutData.position),
      dimensions: { ...activeLayoutData.dimensions },
    };

    if (containerStretch?.enabled) {
      activeScreenLayoutData.position.x = containerStretch.margin;

      activeScreenLayoutData.dimensions.width =
        designSpaceBoundaries.leftX + designSpaceBoundaries.rightX - containerStretch.margin * 2;
    }

    return {
      type: "container-in-section",
      node,
      parentNode,
      rootNode: grandParentNode,
      layout,
      activeLayoutData,
      zIndex: containerZIndex,
      activeScreenLayoutData,
      stretch: containerStretch,
    };
  }, [
    node,
    deviceView,
    designSpaceBoundaries.leftX,
    designSpaceBoundaries.rightX,
    containerStretch,
    parentNode,
    containerZIndex,
  ]);

  if (containerContext == null) {
    return null;
  } else {
    return (
      <SectionEntityPreviewProvider>
        <WebsiteBuilderContainerView containerContext={containerContext}>
          {children}
        </WebsiteBuilderContainerView>
      </SectionEntityPreviewProvider>
    );
  }
}